import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';
import { API_URL } from '../config/api';

const SalaryStructure = () => {
  const navigate = useNavigate();
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalaryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSalaryDetails = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      setLoading(false);
      return;
    }

    const currentUserId = localStorage.getItem('id');
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      setLoading(false);
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'get-salary-details',
        currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.data) {
        setSalaryData(result.data);
      } else {
        toast.error(result?.message || 'Failed to fetch salary details');
      }
    } catch (error) {
      console.error('Error fetching salary details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (withTime) {
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderDetailItem = (label, value) => (
    <div className="employee-detail-item" key={label}>
      <span className="employee-detail-label">{label}</span>
      <span className="employee-detail-value">{value ?? '-'}</span>
    </div>
  );

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Salary Structure
                <i className="fas fa-money-bill-wave ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View your complete salary structure and contribution details.
              </p>
            </div>
            {/* <div className="col-md-4 employee-view-header-actions">
              <button type="button" className="employee-back-btn" onClick={() => navigate('/dashboard')}>
                <i className="fas fa-arrow-left mr-2"></i>
                Back to Dashboard
              </button>
            </div> */}
          </div>

          <div className="card employee-content-card">
            <div className="card-body employee-view-body">
              {loading ? (
                <Loading size={100} />
              ) : !salaryData ? (
                <div className="employee-empty">Salary details are not available.</div>
              ) : (
                <>
                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Basic Information</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('Employee Name', salaryData.name)}
                      {renderDetailItem('Year', salaryData.year)}
                      {renderDetailItem('Status', salaryData.status ? salaryData.status.toUpperCase() : '-')}
                      {renderDetailItem('CTC (Annual)', formatCurrency(salaryData.ctc))}
                      {renderDetailItem('Per Month CTC', formatCurrency(salaryData.perMonthCtc))}
                    </div>
                  </section>

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Earnings</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('Basic', formatCurrency(salaryData.basic))}
                      {renderDetailItem('DA (Dearness Allowance)', formatCurrency(salaryData.da))}
                      {renderDetailItem('HRA (House Rent Allowance)', formatCurrency(salaryData.hra))}
                      {renderDetailItem('Other Allowance', formatCurrency(salaryData.otherAllowance))}
                      {renderDetailItem('Special Allowance', formatCurrency(salaryData.specialAllowance))}
                      {renderDetailItem('Gross Salary', formatCurrency(salaryData.gross))}
                      {renderDetailItem('Basic + DA', formatCurrency(salaryData.basicPlusDa))}
                    </div>
                  </section>

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Deductions (Employee)</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('PF (Employee)', formatCurrency(salaryData.pfEe))}
                      {renderDetailItem('ESI (Employee)', formatCurrency(salaryData.esiEe))}
                      {renderDetailItem('PT (Professional Tax)', formatCurrency(salaryData.pt))}
                      {renderDetailItem('Total Deduction (Employee)', formatCurrency(salaryData.totalDeductionEe))}
                      {renderDetailItem('Net Salary', formatCurrency(salaryData.net))}
                    </div>
                  </section>

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Employer Contributions</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('PF (Employer)', formatCurrency(salaryData.pfEr))}
                      {renderDetailItem('ESI (Employer)', formatCurrency(salaryData.esiEr))}
                      {renderDetailItem('Gratuity', formatCurrency(salaryData.gratuity))}
                    </div>
                  </section>

                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">Additional Information</h3>
                    </div>
                    <div className="employee-detail-grid three-column">
                      {renderDetailItem('Created At', formatDate(salaryData.createdAt, true))}
                      {renderDetailItem('Updated At', formatDate(salaryData.updatedAt, true))}
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalaryStructure;